import type { ReactNode } from 'react'
import { LanguageToggle } from '../LanguageToggle'
import { ThemeToggle } from '../ThemeToggle'
import { cn } from '../../lib/utils'

/**
 * The frame every signed-out screen sits in (spec `2026-09-17-03`).
 *
 * It exists because the login and password-reset routes each hardcoded
 * `bg-gray-50 dark:bg-gray-900` — the only two surfaces in the app outside the
 * token system, so the first screen a prospect sees was a different grey from
 * every page behind it. They also each centred a `max-w-md` column around a
 * card that then added its own `mx-auto mt-8`, which is why the card was never
 * actually centred.
 *
 * One identity flourish: a blurred radial wash of `--gradient-brand` behind
 * the card, clipped to the layout and inert to the pointer. The theme and
 * language switches live in the corner here rather than inside the card —
 * they are chrome, not part of signing in.
 */
export function AuthLayout({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) {
    return (
        <div
            className={cn(
                // `flex-1`, not `min-h-svh`: the app shell's 48px header and
                // its own padding render above this on signed-out routes too,
                // so a full-viewport minimum would put a scrollbar on every
                // login. Filling the space the shell leaves is the same
                // centring without the overflow.
                'relative isolate grid flex-1 place-items-center overflow-hidden bg-background px-4 py-10',
                className,
            )}
            data-testid="auth-layout"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-brand opacity-[0.12] blur-3xl"
            />
            {/* Below `md` only: the shell header's own language/theme pair is
                `hidden md:flex`, so on a phone this corner is the ONLY way to
                switch either — and above `md` a second pair 70px under the
                first just reads as a bug. Either way they are out of the
                card, which is the point (spec 2026-09-17-03). */}
            <div
                className="absolute right-4 top-4 flex items-center gap-1 md:hidden"
                data-testid="auth-chrome"
            >
                <LanguageToggle />
                <ThemeToggle />
            </div>
            {children}
        </div>
    )
}
